import { useEffect, useMemo, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import {
  Users, Trophy, Plus, LogIn, Copy, Crown, Medal, Flame, Star, Clock, RefreshCw, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!target) return "";
  const ms = new Date(target).getTime() - now;
  if (ms <= 0) return "resetting…";
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${d}d ${h}h ${m}m`;
}

export default function TeamLeaderboard() {
  const [teams, setTeams] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [personaFilter, setPersonaFilter] = useState(null);

  const refreshTeams = async () => {
    try {
      const { data } = await http.get("/teams/me");
      setTeams(data);
      if (data.length > 0 && !activeId) setActiveId(data[0].id);
      if (data.length === 0) setActiveId(null);
    } catch { setTeams([]); }
  };

  const refreshBoard = async (id, persona = null) => {
    if (!id) { setBoard(null); return; }
    setLoadingBoard(true);
    try {
      const q = persona ? `?persona=${encodeURIComponent(persona)}` : "";
      const { data } = await http.get(`/teams/${id}/leaderboard${q}`);
      setBoard(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load leaderboard");
    } finally { setLoadingBoard(false); }
  };

  useEffect(() => { refreshTeams(); }, []);
  useEffect(() => { refreshBoard(activeId, personaFilter); }, [activeId, personaFilter]);

  if (teams === null) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 bg-white/10" />
        <Skeleton className="h-32 bg-white/5 rounded-2xl" />
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-300" /> Team Leaderboard
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Rally your rota. Weekly ranking — resets every Monday UTC.
          </p>
        </div>
        {teams.length > 0 && (
          <Button
            variant="outline"
            onClick={() => refreshBoard(activeId, personaFilter)}
            disabled={loadingBoard}
            data-testid="btn-refresh-leaderboard"
            className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingBoard ? "animate-spin" : ""}`} /> Refresh
          </Button>
        )}
      </div>

      {teams.length === 0 ? (
        <EmptyState onCreated={async (t) => { await refreshTeams(); setActiveId(t.id); }} onJoined={async (t) => { await refreshTeams(); setActiveId(t.id); }} />
      ) : (
        <>
          <TeamTabs teams={teams} activeId={activeId} onSelect={setActiveId} onLeft={refreshTeams} />
          {board && (
            <PersonaFilter
              personas={board.personas || {}}
              active={personaFilter}
              onChange={setPersonaFilter}
            />
          )}
          {board && <LeaderboardBody board={board} loading={loadingBoard} />}
          <JoinCreateInline onCreated={async (t) => { await refreshTeams(); setActiveId(t.id); }} onJoined={async (t) => { await refreshTeams(); setActiveId(t.id); }} />
        </>
      )}
    </section>
  );
}

function EmptyState({ onCreated, onJoined }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <CreateTeamCard onCreated={onCreated} />
      <JoinTeamCard onJoined={onJoined} />
    </div>
  );
}

function CreateTeamCard({ onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await http.post("/teams", { name });
      toast.success("Team created", { description: `Join code: ${data.code}` });
      onCreated?.(data);
      setName("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create team");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="circa-card p-6">
      <div className="flex items-center gap-2 mb-3">
        <Plus className="w-4 h-4 text-emerald-300" />
        <div className="font-display text-lg font-semibold">Create a team</div>
      </div>
      <p className="text-slate-400 text-sm mb-4 leading-relaxed">
        Rally your rota. You'll get a 6-character join code to share with colleagues.
      </p>
      <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Team name</Label>
      <Input
        data-testid="input-team-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="ER Night Squad"
        className="mt-1 mb-4 bg-slate-900/60 border-white/10 text-slate-100"
      />
      <Button
        type="submit"
        disabled={busy}
        data-testid="btn-create-team"
        className="w-full rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
      >
        <Plus className="w-4 h-4 mr-1" /> {busy ? "Creating…" : "Create team"}
      </Button>
    </form>
  );
}

function JoinTeamCard({ onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await http.post("/teams/join", { code: code.toUpperCase() });
      toast.success("Joined", { description: data.name });
      onJoined?.(data);
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't join team");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="circa-card p-6">
      <div className="flex items-center gap-2 mb-3">
        <LogIn className="w-4 h-4 text-indigo-300" />
        <div className="font-display text-lg font-semibold">Join a team</div>
      </div>
      <p className="text-slate-400 text-sm mb-4 leading-relaxed">
        Enter the 6-character join code your teammate shared.
      </p>
      <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Code</Label>
      <Input
        data-testid="input-team-code"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="ABC123"
        maxLength={12}
        className="mt-1 mb-4 bg-slate-900/60 border-white/10 text-slate-100 font-mono-ui tracking-widest uppercase"
      />
      <Button
        type="submit"
        disabled={busy}
        data-testid="btn-join-team"
        variant="outline"
        className="w-full rounded-full bg-transparent border-white/15 text-slate-100 hover:bg-white/5"
      >
        <LogIn className="w-4 h-4 mr-1" /> {busy ? "Joining…" : "Join"}
      </Button>
    </form>
  );
}

function JoinCreateInline({ onCreated, onJoined }) {
  const [mode, setMode] = useState(null); // 'create' | 'join' | null
  return (
    <div className="circa-card p-5">
      {mode === null ? (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-400">Need another rota? Add or join a team.</div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setMode("create")} data-testid="btn-open-create-team" variant="outline" className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5">
              <Plus className="w-4 h-4 mr-1" /> Create
            </Button>
            <Button size="sm" onClick={() => setMode("join")} data-testid="btn-open-join-team" className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white">
              <LogIn className="w-4 h-4 mr-1" /> Join
            </Button>
          </div>
        </div>
      ) : mode === "create" ? (
        <CreateTeamCard onCreated={(t) => { setMode(null); onCreated?.(t); }} />
      ) : (
        <JoinTeamCard onJoined={(t) => { setMode(null); onJoined?.(t); }} />
      )}
    </div>
  );
}

function TeamTabs({ teams, activeId, onSelect, onLeft }) {
  const active = teams.find((t) => t.id === activeId);
  const copyCode = () => {
    if (!active) return;
    navigator.clipboard.writeText(active.code).then(
      () => toast.success("Code copied", { description: `Share ${active.code} with colleagues` }),
      () => toast.error("Couldn't copy")
    );
  };
  const leave = async () => {
    if (!active) return;
    if (!window.confirm(`Leave "${active.name}"?`)) return;
    try {
      await http.post(`/teams/${active.id}/leave`);
      toast.success("Left team");
      onLeft?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't leave");
    }
  };

  return (
    <div className="circa-card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-2">
          {teams.map((t) => (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              data-testid={`team-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                t.id === activeId
                  ? "bg-indigo-500/15 text-indigo-200 border border-indigo-400/30"
                  : "bg-slate-900/60 text-slate-400 hover:text-slate-200 border border-white/10"
              }`}
            >
              {t.name}
              <span className="ml-2 text-[10px] font-mono-ui text-slate-500">{t.member_count}</span>
            </button>
          ))}
        </div>
        {active && (
          <div className="flex items-center gap-2">
            <div className="pill bg-slate-900/70 border-white/10 text-slate-300 font-mono-ui tracking-widest" data-testid="team-code">
              {active.code}
            </div>
            <Button size="icon" variant="ghost" onClick={copyCode} data-testid="btn-copy-team-code" className="rounded-full text-slate-300 hover:text-white hover:bg-white/5">
              <Copy className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={leave} data-testid="btn-leave-team" className="rounded-full text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function LeaderboardBody({ board, loading }) {
  const reset = useCountdown(board.reset_at);
  const top3 = board.rows.slice(0, 3);
  const rest = board.rows.slice(3);
  const you = board.rows.find((r) => r.is_you);

  return (
    <div className="space-y-5">
      {/* header strip */}
      <div className="circa-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="pill text-indigo-300 border-indigo-500/30 bg-indigo-950/50 mb-1">
            <Trophy className="w-3.5 h-3.5" /> {board.team.name}
          </div>
          <div className="text-slate-400 text-sm">{board.rows.length} members · rolling 7-day window</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Weekly reset</div>
            <div className="font-display text-lg font-bold text-indigo-300 flex items-center gap-1 justify-end">
              <Clock className="w-4 h-4" /> {reset}
            </div>
          </div>
          {you && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Your rank</div>
              <div className="font-display text-lg font-bold text-emerald-300">#{you.rank}</div>
            </div>
          )}
        </div>
      </div>

      {/* Podium */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {top3.map((r, i) => <PodiumCard key={r.user_id} row={r} place={i + 1} />)}
        </div>
      )}

      {/* Rest of ranks */}
      {rest.length > 0 && (
        <div className="circa-card p-2 sm:p-3 divide-y divide-white/5">
          {rest.map((r) => <RankRow key={r.user_id} row={r} />)}
        </div>
      )}

      {board.rows.length === 0 && (
        <div className="circa-card p-8 text-center text-slate-500">
          Waiting for members. Share your team code to get the leaderboard rolling.
        </div>
      )}
    </div>
  );
}

const PLACE_STYLES = {
  1: { ring: "from-yellow-500 to-amber-300", label: "1st", icon: Crown, tint: "text-yellow-200" },
  2: { ring: "from-slate-400 to-slate-200", label: "2nd", icon: Medal, tint: "text-slate-100" },
  3: { ring: "from-amber-700 to-amber-500", label: "3rd", icon: Medal, tint: "text-amber-300" },
};

function PodiumCard({ row, place }) {
  const s = PLACE_STYLES[place];
  const Icon = s.icon;
  return (
    <div className={`circa-card p-5 relative overflow-hidden ${row.is_you ? "ring-1 ring-indigo-400/40" : ""}`} data-testid={`podium-${place}`}>
      <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none ${place === 1 ? "bg-amber-500/20" : place === 2 ? "bg-slate-400/10" : "bg-amber-700/15"}`} />
      <div className="flex items-start gap-4 relative">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${s.ring} flex items-center justify-center shrink-0`}>
          <Icon className="w-7 h-7 text-white drop-shadow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-mono-ui text-xs uppercase tracking-widest ${s.tint}`}>{s.label}</span>
            {row.is_you && <span className="pill text-[9px] bg-indigo-950/70 border-indigo-500/40 text-indigo-200">You</span>}
          </div>
          <div className="font-display text-xl font-bold truncate">{row.name}</div>
          <div className="text-xs text-slate-400 font-mono-ui uppercase tracking-widest">{row.persona}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Score</div>
          <div className="font-display text-2xl font-extrabold text-indigo-300">{row.score}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 relative">
        <StatChip icon={Flame} tint="text-emerald-300" label="Streak" value={`${row.current_streak}d`} />
        <StatChip icon={Star} tint="text-amber-300" label="Good nts" value={row.weekly.good_nights} />
        <StatChip icon={Clock} tint="text-indigo-300" label="Avg hrs" value={`${row.weekly.avg_hours}h`} />
      </div>
    </div>
  );
}

function RankRow({ row }) {
  return (
    <div
      className={`flex items-center gap-4 px-3 py-3 rounded-xl transition ${
        row.is_you ? "bg-indigo-500/10 border border-indigo-400/30" : "hover:bg-white/[0.03]"
      }`}
      data-testid={`rank-row-${row.rank}`}
    >
      <div className="w-8 text-center font-mono-ui text-slate-400 text-sm">#{row.rank}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-100 truncate">{row.name}</span>
          {row.is_you && <span className="pill text-[9px] bg-indigo-950/70 border-indigo-500/40 text-indigo-200">You</span>}
          <span className="pill text-[9px] bg-slate-900/60 border-white/10 text-slate-400">{row.persona}</span>
        </div>
        <div className="text-xs text-slate-500 font-mono-ui mt-0.5">
          {row.weekly.good_nights} good nts · {row.weekly.avg_hours}h avg · q {row.weekly.avg_quality}/5
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right hidden sm:block">
          <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Streak</div>
          <div className="text-emerald-300 font-display font-bold">{row.current_streak}d</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Score</div>
          <div className="text-indigo-300 font-display font-bold">{row.score}</div>
        </div>
      </div>
    </div>
  );
}

function PersonaFilter({ personas, active, onChange }) {
  const entries = Object.entries(personas || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length <= 1) return null;
  const total = entries.reduce((n, [, c]) => n + c, 0);
  return (
    <div className="circa-card p-4 flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500 mr-1">Filter by persona</span>
      <button
        onClick={() => onChange(null)}
        data-testid="persona-filter-all"
        className={`pill ${active === null ? "bg-indigo-950/70 border-indigo-500/40 text-indigo-200" : "bg-slate-900/60 border-white/10 text-slate-400 hover:text-slate-200"}`}
      >
        All · {total}
      </button>
      {entries.map(([p, count]) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          data-testid={`persona-filter-${p}`}
          className={`pill ${active === p ? "bg-indigo-950/70 border-indigo-500/40 text-indigo-200" : "bg-slate-900/60 border-white/10 text-slate-400 hover:text-slate-200"}`}
        >
          {p.replace(/_/g, " ")} · {count}
        </button>
      ))}
    </div>
  );
}


function StatChip({ icon: Icon, tint, label, value }) {
  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-xl p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">
        <Icon className={`w-3 h-3 ${tint}`} /> {label}
      </div>
      <div className={`font-display text-lg font-bold ${tint}`}>{value}</div>
    </div>
  );
}
